import { useMutation, useQuery } from "@tanstack/react-query";
import { makeAuthedRequest } from "shared/api/authRequest";
import { SiteResponse } from "api/ApiClient";
import type { ActivationCheckResponse } from "shared/types/ActivationCheckResponse";

// Lifecycle status of a Property. Mirrors ConnectApi's PropertyStatus enum
// (src/Livly.Connect.Models/Enums/Enums.cs) -- not yet in the NSwag-generated client.
export type PropertyStatus = "Staging" | "Active" | "Archived";

export const PROPERTY_STATUS_OPTIONS: PropertyStatus[] = ["Staging", "Active", "Archived"];

// Types based on swagger definitions
export interface PropertyAccessGroupLockResponse {
  needsUpdated?: boolean | null;
  id: string;
  name?: string | null;
}

export interface BasicPropertyUserResponse {
  id?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

export type AccessControlGroupType =
  | "regular"
  | "defaultForProperty"
  | "commonDoors"
  | "master"
  | "unit"
  | "defaultForGuests"
  | "defaultForStaff"
  | "staffOnly";

export interface PropertyAccessControlGroupResponse {
  id: string;
  externalId?: string | null;
  propertyExternalId?: string | null;
  unitNumber?: string | null;
  type: AccessControlGroupType;
  name?: string | null;
  description?: string | null;
  locks?: PropertyAccessGroupLockResponse[] | null;
  createdTimestamp: string;
  createdBy?: BasicPropertyUserResponse;
  updateTimestamp: string;
  updatedBy?: BasicPropertyUserResponse;
  schedules?: string[] | null;
  needsUpdated?: boolean | null;
}

export interface GuestPropertyDetailResponse {
  id?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  hasActivatedKey: boolean;
  physicalKeys?: string[] | null;
}

export interface PropertyConfigResponse {
  propertyExternalId?: string | null;
  name?: string | null;
  physicalKeyExtension?: boolean | null;
  status?: PropertyStatus | null;
}

export interface PropertyCreateRequest {
  propertyExternalId: string;
  name: string;
  physicalKeyExtension?: boolean | null;
}

export interface PropertyUpdateRequest {
  name: string;
  physicalKeyExtension?: boolean | null;
}

export interface PropertyStatusUpdateRequest {
  status: PropertyStatus;
}

export type { ActivationCheckResponse };

export interface PropertyUnitPersonResponse {
  id: string;
  firstName: string;
  lastName: string;
}

export interface PropertyUnitGroupResponse {
  id: string;
  name: string;
}

export interface PropertyUnitLockResponse {
  id: string;
  name: string;
}

export interface PropertyUnitSummaryResponse {
  unitNumber: string;
  guests: PropertyUnitPersonResponse[];
  staff: PropertyUnitPersonResponse[];
  groups: PropertyUnitGroupResponse[];
  locks: PropertyUnitLockResponse[];
}

export function useCreateProperty() {
  return useMutation({
    mutationFn: (payload: PropertyCreateRequest) =>
      makeAuthedRequest<PropertyConfigResponse>({
        method: "post",
        url: "/api/Property",
        data: payload,
      }),
  });
}

export function useUpdateProperty(propertyExternalId: string) {
  return useMutation({
    mutationFn: (payload: PropertyUpdateRequest) =>
      makeAuthedRequest<PropertyConfigResponse>({
        method: "put",
        url: `/api/Property/${propertyExternalId}`,
        data: payload,
      }),
  });
}

export function usePropertyConfig(propertyExternalId: string) {
  return useQuery({
    queryKey: ["property-config", propertyExternalId],
    queryFn: () =>
      makeAuthedRequest<PropertyConfigResponse>({
        method: "get",
        url: `/api/Property/${propertyExternalId}/config`,
      }),
    enabled: !!propertyExternalId,
  });
}

// Staging -> Active guardrail check (informational only, never blocks).
export function usePropertyActivationCheck(propertyExternalId: string, enabled = false) {
  return useQuery({
    queryKey: ["property-activation-check", propertyExternalId],
    queryFn: () =>
      makeAuthedRequest<ActivationCheckResponse>({
        method: "get",
        url: `/api/Property/${propertyExternalId}/status/activation-check`,
      }),
    enabled: enabled && !!propertyExternalId,
  });
}

export function useUpdatePropertyStatus(propertyExternalId: string) {
  return useMutation({
    mutationFn: (payload: PropertyStatusUpdateRequest) =>
      makeAuthedRequest<PropertyConfigResponse>({
        method: "patch",
        url: `/api/Property/${propertyExternalId}/status`,
        data: payload,
      }),
  });
}

// Queues guest access reconciliation for every guest with existing GuestPropertyAccess records at this
// property. The actual work runs in the background; progress/results can be tracked via the property's
// sync records (see PropertySyncRecords.tsx).
export function useReonboardProperty(propertyExternalId: string) {
  return useMutation({
    mutationFn: () =>
      makeAuthedRequest<string>({
        method: "post",
        url: `/api/Property/${propertyExternalId}/reonboard`,
      }),
  });
}

export function usePropertyAccessControlGroups(propertyExternalId: string) {
  return useQuery({
    queryKey: ["property-access-control-groups", propertyExternalId],
    queryFn: () =>
      makeAuthedRequest<PropertyAccessControlGroupResponse[]>({
        method: "get",
        url: `/api/Property/${propertyExternalId}/access-control-groups`,
      }),
    enabled: !!propertyExternalId,
  });
}

export function usePropertyGuests(propertyExternalId: string) {
  return useQuery({
    queryKey: ["property-guests", propertyExternalId],
    queryFn: () =>
      makeAuthedRequest<GuestPropertyDetailResponse[]>({
        method: "get",
        url: `/api/Property/${propertyExternalId}/guests`,
      }),
    enabled: !!propertyExternalId,
  });
}

export function usePropertySites(propertyExternalId: string) {
  return useQuery({
    queryKey: ["property-sites", propertyExternalId],
    queryFn: () =>
      makeAuthedRequest<SiteResponse[]>({
        method: "get",
        url: `/api/Property/${propertyExternalId}/sites`,
      }),
    enabled: !!propertyExternalId,
  });
}

export function usePropertyUnits(propertyExternalId: string) {
  return useQuery({
    queryKey: ["property-units", propertyExternalId],
    queryFn: () =>
      makeAuthedRequest<PropertyUnitSummaryResponse[]>({
        method: "get",
        url: `/api/Property/${propertyExternalId}/units`,
      }),
    enabled: !!propertyExternalId,
  });
}
